import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MetricConfig } from "@/types/template.types";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { Info } from "lucide-react";

interface MetricProgressProps {
  value: number;
  config: MetricConfig;
}

export function MetricProgress({ value, config }: MetricProgressProps) {
  const getProgressValue = (value: number, config: MetricConfig) => {
    const { min, max } = config.thresholds;

    // Special handling for metrics where min equals max
    // if (min === max) {
    //   return value >= min ? 100 : 0;
    // }

    // For metrics where min is greater than 0, we want to show some progress
    // even when the value equals the min
    // if (min > 0) {
    //   // Start from 20% when value equals min, go up to 100% at max
    //   const baseProgress = 20;
    //   const remainingProgress = 100 - baseProgress;
    //   const normalizedValue =
    //     ((value - min) / (max - min)) * remainingProgress + baseProgress;
    //   return Math.min(Math.max(normalizedValue, baseProgress), 100);
    // }

    // Ensure the progress value is between 0 and 100
    const normalizedValue = ((value - min) / (max - min)) * 100;
    return Math.min(Math.max(normalizedValue, 0), 100);
  };

  const getProgressColor = (value: number, config: MetricConfig) => {
    const { min, max, warningThreshold } = config.thresholds;

    // Special handling for spam score
    if (config.label === "Spam Score") {
      if (value <= 40) return "bg-green-500";
      if (value <= 60) return "bg-yellow-500";
      return "bg-red-500";
    }

    // For metrics where lower is better (like spam words, broken links, etc.)
    if (min === 0 && max === 0) {
      if (value === 0) return "bg-green-500";
      if (value <= (warningThreshold || 1)) return "bg-yellow-500";
      return "bg-red-500";
    }

    // For metrics with a recommended range
    if (value >= min && value <= max) {
      return "bg-green-500";
    }
    if (value <= (warningThreshold || max * 1.2)) {
      return "bg-yellow-500";
    }
    return "bg-red-500";
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Label>{config.label}</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="size-3 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white max-w-[280px]">
              <TooltipArrow />
              <p>{config.description}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <Badge variant="secondary">
          {value}
          {config.unit && ` ${config.unit}`}
        </Badge>
      </div>
      <Progress
        value={getProgressValue(value, config)}
        className="bg-slate-100 h-2"
        indicatorClassName={cn("duration-300", getProgressColor(value, config))}
      />
    </div>
  );
}
